<script setup>
import Vue, {computed, ref, watch, inject} from "vue";
import trackersReportsService from "@/services/trackers-reports.service";
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import AddList from "@/components/trackers/comparisonReport/AddList.vue";
import {useStore} from "vue2-helpers/vuex";
import {validEmail} from "@/helpers/string";
import useStoreTrigger from "@/composables/useStoreTrigger";
import {trackerTypes} from "@/constants/trackers";

const emit = defineEmits(['openReport']);
const store = useStore();
const storeTrigger = useStoreTrigger();

const tracker = inject("tracker").value;
const selectedRows = inject("selectedRows");

const showModal = ref(false);
const selectedFeatures = ref([]);
const recommendedFeatures = ref([]);
const recommendedFeaturesLoading = ref(true);
const profileFields = ref([]);
const diligenceFields = ref([]);
const filter = ref("");

const loading = ref(false);
const reportId = ref(null);
const interval = ref(null);
const step = ref(1);
const email = ref('');

const standardFeatures = ref([
    'Short Description',
    'Employee Count',
    'Total Raised',
    'Clients'
]);

const modalTitle = computed(() => {
    return step.value === 1 ? "Select Features" : "Report Generating";
});

const suggestedLists = computed(() => [...recommendedFeatures.value, ...standardFeatures.value, ...profileFields.value, ...diligenceFields.value]);

const actualRecommendedFeatures = computed(() => {
    return filteredList(recommendedFeatures.value);
});

const actualStandardFeatures = computed(() => {
    return filteredList(standardFeatures.value);
});

const actualProfileFields = computed(() => {
    return filteredList(profileFields.value);
});

const actualDiligenceFields = computed(() => {
    return filteredList(diligenceFields.value);
});

const filterHasExactMatch = computed(() => suggestedLists.value.some(feature => feature.toLowerCase() === filter.value.toLowerCase()));

watch(showModal, (value) => {
    if (!value) {
        clearInterval(interval.value);
    }
});

function filteredList(list) {
    return list
        .filter(feature => !selectedFeatures.value.some(selectedFeature => feature.toLowerCase() === selectedFeature.toLowerCase()))
        .filter(feature => feature.toLowerCase().includes(filter.value.toLowerCase()));
}

async function sendRequest() {
    loading.value = true;
    const {data} = await trackersReportsService.requestComparisonReport(tracker.id, selectedRows.value, selectedFeatures.value);

    loading.value = false;

    if (data) {
        let [ready, report_id] = data;

        if (ready) {
            showModal.value = false;

            emit('openReport', {
                id: report_id,
                text: 'Feature Comparison Report',
            });

            storeTrigger('setNeedUpdateTrackerLogs');
        } else {
            reportId.value = report_id;
            step.value = 2;
        }
    } else {
        Vue.notify({
            group: 'app',
            type: 'error',
            text: 'Error occurred'
        });
    }
}

async function sendEmail() {
    loading.value = true;
    const {data} = await trackersReportsService.saveComparisonReportEmail(reportId.value, email.value);

    loading.value = false;
    showModal.value = false;

    if (data) {
        Vue.notify({
            group: 'app',
            type: 'success',
            text: 'Request sent'
        });
    } else {
        Vue.notify({
            group: 'app',
            type: 'error',
            text: 'Error occurred'
        });
    }
}

async function getRecommendedFeatures() {
    const {data} = await trackersReportsService.getRecommendedFeatures(selectedRows.value);
    recommendedFeatures.value = data;
    recommendedFeaturesLoading.value = false;
}

async function getCustomFields() {
    const {data} = await trackersReportsService.getCustomFields(tracker.id);
    profileFields.value = data[0];
    diligenceFields.value = data[1];
}

function addCustomFeature() {
    if (filter.value.length) {
        selectedFeatures.value.push(filter.value);
        filter.value = "";
    }
}

function addFeature(feature) {
    selectedFeatures.value.push(feature);
    filter.value = "";
}

function open() {
    if (tracker.type !== trackerTypes.POC && !standardFeatures.value.includes('Funnel Stage')) {
        standardFeatures.value.push('Funnel Stage');
    }

    if (tracker.type === trackerTypes.POC && !standardFeatures.value.includes('POC Status')) {
        standardFeatures.value.push('POC Status');
    }

    selectedFeatures.value = [...standardFeatures.value];
    recommendedFeatures.value = [];
    recommendedFeaturesLoading.value = true;
    filter.value = '';
    reportId.value = null;
    email.value = store.state.user.email;
    step.value = 1;
    showModal.value = true;

    getRecommendedFeatures();
    getCustomFields();
}

defineExpose({
    open
});

</script>

<template>
    <b-modal v-model="showModal" :title="modalTitle" modal-class="modal-select-features modal-w-xl">
        <template v-if="step === 1">
            <p class="mb-4">Add features you want to compare</p>

            <div class="content">
<!-- Left side -->
                <div class="content__col">
                    <div>
                        <input
                            v-model="filter"
                            type="text"
                            placeholder="Enter feature name"
                            class="form-control form-control--mid"
                            @keyup.enter="addCustomFeature"
                        >
                    </div>

                    <AddList
                        v-if="filter.length && !filterHasExactMatch"
                        title="Add Column Feature"
                        :list="[filter]"
                        @add="addFeature"
                    />

                    <AddList
                        v-show="actualRecommendedFeatures.length || recommendedFeaturesLoading"
                        title="Suggestions"
                        :list="actualRecommendedFeatures"
                        :ai-generated="true"
                        :loading="recommendedFeaturesLoading"
                        @add="addFeature"
                    />

                    <AddList
                        v-show="actualStandardFeatures.length"
                        title="Standard Fields"
                        :list="actualStandardFeatures"
                        @add="addFeature"
                    />

                    <AddList
                        v-show="actualProfileFields.length"
                        title="Your Profile Fields"
                        :list="actualProfileFields"
                        :collapsed="true"
                        @add="addFeature"
                    />

                    <AddList
                        v-show="actualDiligenceFields.length"
                        title="Your Startup Diligence Fields"
                        :list="actualDiligenceFields"
                        :collapsed="true"
                        @add="addFeature"
                    />
                </div>
<!-- Right side -->
                <div class="content__col">
                    <div class="selected-block">
                        <div class="selected-block__header">
                            Selected
                        </div>

                        <div class="selected-block__content">
                            <ul v-if="selectedFeatures.length" class="selected-block__list">
                                <li
                                    v-for="(feature, index) in selectedFeatures"
                                    :key="index"
                                    
                                >
                                    <div>
                                        {{feature}}
                                    </div>

                                    <a @click="selectedFeatures.splice(index, 1)" class="remove-button"></a>
                                </li>
                            </ul>

                            <div v-else class="text-center">
                                No features selected.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template v-else-if="step === 2">
            <div class="w-sm-75 text-modal">
                <p>Creating this report will take about 10 minutes. Add your email and we’ll send it to you when it’s ready.</p>
            </div>

            <div class="w-sm-50">
                <input
                    v-model="email"
                    type="text"
                    placeholder="Enter email"
                    class="form-control form-control--mid"
                    @keyup.enter="addCustomFeature"
                >
            </div>
        </template>

        <template #modal-footer>
            <div v-if="step === 2">
                <PrimaryButton
                    :loading="loading"
                    :disabled="!(email.length && validEmail(email))"
                    caption="Email Me"
                    loading-caption="Saving email..."
                    @click="sendEmail"
                />
            </div>

            <PrimaryButton
                v-else
                :loading="loading"
                :disabled="!selectedFeatures.length"
                caption="Generate Report"
                loading-caption="Sending request..."
                @click="sendRequest"
            />
        </template>
    </b-modal>
</template>

<style scoped>
</style>
